import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AggiungiMedia() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState(null);
    const cancelled = params.get("cancelled") === "1";

    useEffect(() => {
        if (!id) return;
        api.get(`/bookings/${id}/media-info`)
            .then(r => setInfo(r.data))
            .catch(e => setError(e?.response?.data?.detail || "Booking non trovato"))
            .finally(() => setLoading(false));
    }, [id]);

    const paga = async () => {
        setPaying(true);
        try {
            // V55.8 · Se veniamo da un link fotografo con ?slot=xxx, includiamolo nella query
            const slotId = params.get("slot") || "";
            const qs = slotId ? `?slot=${encodeURIComponent(slotId)}` : "";
            const r = await api.post(`/bookings/${id}/add-media${qs}`, { origin_url: window.location.origin });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore checkout");
            setPaying(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#0f1116] flex items-center justify-center text-[#9AA2AD]">
            Caricamento…
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-[#0f1116] flex items-center justify-center px-4">
            <div className="max-w-md text-center border border-[#E2231A]/40 p-8">
                <h1 className="font-display text-2xl text-[#E2231A] uppercase tracking-widest mb-3">Link non valido</h1>
                <p className="text-[#9AA2AD] text-sm">{error}</p>
                <Link to="/" className="inline-block mt-4 border border-[#9AA2AD]/30 px-4 py-2 text-xs uppercase tracking-widest text-[#F4F2ED] hover:border-[#E2231A]">Torna alla home</Link>
            </div>
        </div>
    );

    const price = info.prevendita_active ? info.bundle_amount_cents / 100 : info.bundle_list_price_cents / 100;
    const listPrice = info.bundle_list_price_cents / 100;

    return (
        <div className="min-h-screen bg-[#0f1116] text-[#F4F2ED] flex items-center justify-center px-4 py-10">
            <div className="max-w-lg w-full">
                <div className="text-center mb-8">
                    <Logo variant="light" className="h-12 inline-block" />
                </div>

                {cancelled && (
                    <div className="mb-4 border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 text-[#f59e0b] text-xs uppercase tracking-widest text-center" data-testid="cancelled-banner">
                        Pagamento annullato — puoi riprovare quando vuoi
                    </div>
                )}

                <div className="bg-[#15181C] border border-[#E2231A]/30 p-8 text-center" data-testid="media-landing-card">
                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Ciao {info.customer_name || "Pilota"}, voucher <b className="text-[#F4F2ED]">{info.voucher}</b></div>
                    <h1 className="font-display text-3xl md:text-4xl uppercase tracking-widest text-[#E2231A] mt-4">🎬 Pacchetto Foto+Video</h1>
                    <p className="text-[#9AA2AD] mt-4 text-sm leading-relaxed">Aggiungi il pacchetto media al tuo turno in pista e ricevi contenuti professionali entro 5 giorni dall'evento.</p>

                    {(info.has_media && !info.already_purchased) && (
                        <div className="my-6" data-testid="media-preview-block">
                            <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] mb-2">
                                🔒 {info.photos_count || 0} foto{info.videos_count ? ` · ${info.videos_count} video` : ""} pronti — anteprima con filigrana
                            </div>
                            {(info.photos_preview || []).length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="media-preview-grid">
                                    {(info.photos_preview || []).slice(0, 6).map((u, i) => (
                                        <div key={i} className="relative overflow-hidden rounded bg-[#0B0D10] aspect-square">
                                            <img src={u} alt={`anteprima ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {info.videos_count > 0 && (
                                <div className="mt-2 text-[11px] text-[#9AA2AD] border border-[#E2231A]/30 bg-[#E2231A]/5 py-2 px-3">
                                    🎬 <b className="text-[#F4F2ED]">{info.videos_count} video HD</b> pronti — si sbloccano dopo il pagamento
                                </div>
                            )}
                            <div className="mt-2 text-[10px] text-[#9AA2AD]">Sblocca tutto in alta risoluzione, <b className="text-[#16a34a]">senza filigrana</b>, dopo il pagamento.</div>
                        </div>
                    )}

                    <div className="my-8">
                        <ul className="text-left text-sm space-y-2 max-w-sm mx-auto">
                            {(info.bundle_features || []).map((f, i) => (
                                <li key={i} className="flex items-start gap-2"><span className="text-[#E2231A] mt-0.5">✓</span> {f}</li>
                            ))}
                        </ul>
                    </div>

                    {info.already_purchased ? (
                        <div className="bg-[#16a34a]/10 border border-[#16a34a] p-6 my-6" data-testid="already-purchased">
                            <div className="text-[#16a34a] font-display text-xl uppercase tracking-widest">✓ Già acquistato</div>
                            <div className="text-[#9AA2AD] text-xs mt-2">Il pacchetto Foto+Video è già nel tuo ordine.</div>
                        </div>
                    ) : (
                        <>
                            <div className="my-8">
                                <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Prezzo {info.prevendita_active ? "prevendita" : "listino"}</div>
                                <div className="font-display text-6xl text-[#E2231A]">€{price.toFixed(0)}</div>
                                {info.prevendita_active && (
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mt-1">Listino <span className="line-through">€{listPrice.toFixed(0)}</span> — <span className="text-[#16a34a]">risparmi €{(listPrice - price).toFixed(0)}</span></div>
                                )}
                            </div>
                            <button
                                onClick={paga}
                                disabled={paying}
                                data-testid="btn-paga-media"
                                className="w-full bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-40 text-[#F4F2ED] font-display uppercase tracking-widest text-sm py-4 transition-colors"
                            >
                                {paying ? "Attendi…" : `Aggiungi ora · €${price.toFixed(0)}`}
                            </button>
                            <div className="text-[9px] text-[#9AA2AD] uppercase tracking-widest mt-3">Pagamento sicuro Stripe · Fattura via email</div>
                        </>
                    )}
                </div>

                <div className="text-center mt-6 text-[10px] text-[#9AA2AD] uppercase tracking-widest">
                    VELORA JDM Experience · MAS S.r.l.
                </div>
            </div>
        </div>
    );
}

export function AggiungiMediaSuccess() {
    const [params] = useSearchParams();
    const bid = params.get("bid");
    return (
        <div className="min-h-screen bg-[#0f1116] text-[#F4F2ED] flex items-center justify-center px-4">
            <div className="max-w-md text-center border border-[#16a34a] p-8" data-testid="media-success">
                <div className="mb-4"><Logo variant="light" className="h-12 inline-block" /></div>
                <div className="text-[#16a34a] text-6xl mb-4">✓</div>
                <h1 className="font-display text-2xl uppercase tracking-widest text-[#16a34a] mb-3">Grazie!</h1>
                <p className="text-[#9AA2AD] text-sm leading-relaxed">Il tuo pacchetto Foto+Video è stato aggiunto. Riceverai una email di conferma con la ricevuta. I contenuti arriveranno entro 5 giorni dopo l'evento.</p>
                {bid && <Link to={`/tunnel/${bid}`} className="inline-block mt-6 border border-[#E2231A] hover:bg-[#E2231A]/10 px-6 py-3 text-xs uppercase tracking-widest">Torna al Check-in Online</Link>}
            </div>
        </div>
    );
}
